import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { BackButton, PageLoader, Spinner } from "@andrum/ui";
import {
  useOnboardingStore,
  LAST_STEP,
} from "@/stores/onboarding-store";
import { useAuthStore } from "@/stores/auth-store";
import { useExperimentStore } from "@/stores/experiment-store";
import { durations, easings } from "@/lib/motion/tokens";
import { hapticService } from "@/lib/capacitor";
import { cn } from "@/lib/utils";
import { completeOnboardingRemote } from "@/features/auth/api/auth-api";
import { isAuthEnforced } from "@/lib/supabase/client";
import type {
  GoalCode,
  LifeSituation,
  CoachTone,
  AvailableMinutes,
  PreferredTime,
  PathwayStyle,
  Scale,
} from "@andrum/schemas";
import { goalsNeedPathway } from "@andrum/schemas";
import { ScaleSlider } from "@/features/check-in/components/ScaleSlider";

/** 0 welcome · 1 method · 2 goals · 3 pathway · 4 life · 5 baseline · 6 time · 7 tone · 8 when · 9 notifs */
const TOTAL_STEPS = LAST_STEP + 1;

const GOAL_CODES: GoalCode[] = [
  "energy",
  "joy",
  "calm",
  "connection",
  "meaning",
  "alone_time",
];

const LIFE_VALUES: LifeSituation[] = [
  "working",
  "studying",
  "parental_leave",
  "parent_of_small_children",
  "between_jobs",
  "retired",
  "other",
  "prefer_not_to_say",
];

const PATHWAY_VALUES: PathwayStyle[] = ["social", "solo", "both"];

const TONE_VALUES: CoachTone[] = [
  "warm_encouraging",
  "direct_practical",
  "reflective_calm",
];

const TIME_VALUES: AvailableMinutes[] = ["2", "5", "10", "15+"];

const PREFERRED_VALUES: PreferredTime[] = [
  "morning",
  "lunch",
  "afternoon",
  "evening",
  "varies",
];

function parseAvailableMinutes(
  value: AvailableMinutes | undefined
): number | undefined {
  if (!value) return undefined;
  if (value === "15+") return 15;
  return Number.parseInt(value, 10);
}

function WhyNote({ children }: { children: string }) {
  return (
    <p className="rounded-xl border border-border bg-primary-soft/40 px-3 py-2 text-xs leading-snug text-foreground-muted">
      {children}
    </p>
  );
}

/** Soft crossfade only — no layout shift / horizontal jump between steps. */
const stepMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export function OnboardingScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const store = useOnboardingStore();
  const setProfile = useAuthStore((s) => s.setProfile);
  const hydrateExperiments = useExperimentStore((s) => s.hydrate);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const {
    step,
    goals,
    nextStep,
    prevStep,
    toggleGoal,
    toggleLifeSituation,
    setJoyPathway,
    setCalmPathway,
    setBaseline,
    setAvailableMinutes,
    setCoachTone,
    setPreferredTime,
    setNotificationsEnabled,
    complete,
  } = store;
  const baseline = store.baseline;
  const needPath = goalsNeedPathway(goals);
  const lifeSituations = store.lifeSituations ?? [];

  const goalOptions = useMemo(
    () =>
      GOAL_CODES.map((code) => ({
        code,
        label: t(`onboarding.goals.${code}`),
      })),
    [t]
  );

  const lifeOptions = useMemo(
    () =>
      LIFE_VALUES.map((value) => ({
        value,
        label: t(`onboarding.life.${value}`),
      })),
    [t]
  );

  const pathwayOptions = useMemo(
    () =>
      PATHWAY_VALUES.map((value) => ({
        value,
        label: t(`onboarding.pathway.${value}`),
        desc: t(`onboarding.pathway.${value}_desc`),
      })),
    [t]
  );

  const toneOptions = useMemo(
    () =>
      TONE_VALUES.map((value) => ({
        value,
        label: t(`onboarding.tones.${value}`),
        desc: t(`onboarding.tones.${value}_desc`),
      })),
    [t]
  );

  const timeOptions = useMemo(
    () =>
      TIME_VALUES.map((value) => ({
        value,
        label: t(`onboarding.minutes.${value}`),
      })),
    [t]
  );

  const preferredTimeOptions = useMemo(
    () =>
      PREFERRED_VALUES.map((value) => ({
        value,
        label: t(`onboarding.preferred.${value}`),
      })),
    [t]
  );

  const methodPillars = useMemo(
    () =>
      [1, 2, 3, 4].map((n) => ({
        title: t(`onboarding.pillar${n}Title`),
        body: t(`onboarding.pillar${n}Body`),
      })),
    [t]
  );

  const handleNext = () => {
    void hapticService.selection();
    if (step !== LAST_STEP) {
      nextStep();
      return;
    }

    void (async () => {
      setSaveError(null);
      complete();

      if (
        isAuthEnforced() &&
        useAuthStore.getState().status === "authenticated"
      ) {
        setSaving(true);
        const result = await completeOnboardingRemote({
          preferredCoachTone: store.coachTone,
          availableMinutes: parseAvailableMinutes(store.availableMinutes),
          goals: store.goals,
          lifeSituations: store.lifeSituations ?? [],
          joyPathway: store.goals.includes("joy")
            ? (store.joyPathway ?? null)
            : null,
          calmPathway: store.goals.includes("calm")
            ? (store.calmPathway ?? null)
            : null,
        });
        if (!result.ok) {
          setSaving(false);
          setSaveError(result.message);
          return;
        }
        setProfile(result.data);
      }

      // Prefetch today data while we show the finishing loader
      setSaving(true);
      await Promise.allSettled([hydrateExperiments()]);
      setSaving(false);
      void navigate({ to: "/app/today" });
    })();
  };

  const canProceed = () => {
    if (step === 2) return goals.length > 0;
    if (step === 3) {
      if (needPath.joy && !store.joyPathway) return false;
      if (needPath.calm && !store.calmPathway) return false;
      return true;
    }
    if (step === 5) {
      return (
        baseline?.wellbeing != null &&
        baseline?.energy != null &&
        baseline?.stress != null
      );
    }
    if (step === 6) return !!store.availableMinutes;
    if (step === 7) return !!store.coachTone;
    if (step === 8) return !!store.preferredTime;
    return true;
  };

  const updateBaseline = (
    key: "wellbeing" | "energy" | "stress",
    value: Scale
  ) => {
    setBaseline({
      wellbeing: key === "wellbeing" ? value : (baseline?.wellbeing ?? 5),
      energy: key === "energy" ? value : (baseline?.energy ?? 5),
      stress: key === "stress" ? value : (baseline?.stress ?? 5),
      connection: baseline?.connection ?? 5,
      recovery: baseline?.recovery ?? 5,
      meaning: baseline?.meaning ?? 5,
    });
  };

  const nextLabel = saving
    ? t("onboarding.saving")
    : step === LAST_STEP
      ? t("onboarding.startApp")
      : step === 0
        ? t("onboarding.begin")
        : step === 1
          ? t("onboarding.methodCta")
          : t("next");

  if (saving) {
    return (
      <div className="flex h-dvh flex-col overflow-hidden bg-background">
        <PageLoader label={t("onboarding.saving")} />
      </div>
    );
  }

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-hidden overscroll-none bg-background">
      <div className="shrink-0 px-5 pt-[calc(env(safe-area-inset-top)+12px)] pb-1.5">
        <div className="mb-2 flex items-center justify-between">
          {step > 0 ? (
            <BackButton
              onClick={() => {
                void hapticService.selection();
                prevStep();
              }}
            >
              {t("onboarding.back")}
            </BackButton>
          ) : (
            <span />
          )}
          <span className="tabular-nums text-xs text-foreground-subtle">
            {t("onboarding.progress", {
              current: step + 1,
              total: TOTAL_STEPS,
            })}
          </span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-surface-muted">
          <motion.div
            className="h-full rounded-full bg-primary"
            initial={false}
            animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
            transition={{ duration: durations.normal, ease: easings.standard }}
          />
        </div>
      </div>

      {/* Prefer fit-to-screen; scroll if a step still overflows (e.g. small phones). */}
      <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-5 py-3 [-webkit-overflow-scrolling:touch]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            className="flex min-h-full flex-col justify-center gap-2.5"
            {...(reduce
              ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 1 } }
              : {
                  ...stepMotion,
                  transition: {
                    duration: durations.fast,
                    ease: easings.standard,
                  },
                })}
          >
            {step === 0 && (
              <>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                  {t("onboarding.welcomeKicker")}
                </p>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.welcomeTitle")}
                </h1>
                <p className="text-sm leading-snug text-foreground-muted">
                  {t("onboarding.welcomeBody")}
                </p>
                <div className="space-y-1.5 rounded-xl border border-border bg-surface-raised p-3">
                  <p className="text-sm font-medium text-foreground">
                    {t("onboarding.problemTitle")}
                  </p>
                  <ul className="space-y-1 text-xs leading-snug text-foreground-muted">
                    <li>· {t("onboarding.problem1")}</li>
                    <li>· {t("onboarding.problem2")}</li>
                    <li>· {t("onboarding.problem3")}</li>
                  </ul>
                </div>
                <p className="text-xs leading-snug text-foreground-subtle">
                  {t("onboarding.welcomeFooter")}
                </p>
              </>
            )}

            {step === 1 && (
              <>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                  {t("onboarding.methodKicker")}
                </p>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.methodTitle")}
                </h1>
                <p className="text-sm leading-snug text-foreground-muted">
                  {t("onboarding.methodBody")}
                </p>
                <div className="grid min-h-0 grid-cols-2 gap-2">
                  {methodPillars.map((p) => (
                    <div
                      key={p.title}
                      className="rounded-xl border border-border bg-surface-raised px-2.5 py-2"
                    >
                      <p className="text-xs font-medium leading-snug text-foreground">
                        {p.title}
                      </p>
                      <p className="mt-0.5 text-[11px] leading-snug text-foreground-muted">
                        {p.body}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs leading-snug text-foreground-subtle">
                  {t("onboarding.methodFooter")}
                </p>
              </>
            )}

            {step === 2 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.goalsTitle")}
                </h1>
                <WhyNote>{t("onboarding.goalsWhy")}</WhyNote>
                <p className="text-xs text-foreground-muted">
                  {t("onboarding.goalsHint")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {goalOptions.map((g) => {
                    const selected = goals.includes(g.code);
                    return (
                      <button
                        key={g.code}
                        type="button"
                        onClick={() => {
                          void hapticService.selection();
                          toggleGoal(g.code);
                        }}
                        className={cn(
                          "rounded-xl border px-2.5 py-3 text-center text-sm font-medium transition-colors",
                          selected
                            ? "border-primary bg-primary-soft text-primary"
                            : "border-border bg-surface-raised text-foreground"
                        )}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.pathwayTitle")}
                </h1>
                <WhyNote>{t("onboarding.pathwayWhy")}</WhyNote>
                <div className="min-h-0 space-y-3 overflow-y-auto">
                  {needPath.joy ? (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-foreground">
                        {t("onboarding.pathwayJoyLabel")}
                      </p>
                      <div className="space-y-1.5">
                        {pathwayOptions.map((opt) => (
                          <button
                            key={`joy-${opt.value}`}
                            type="button"
                            onClick={() => {
                              void hapticService.selection();
                              setJoyPathway(opt.value);
                            }}
                            className={cn(
                              "w-full rounded-xl border px-3 py-2 text-left transition-colors",
                              store.joyPathway === opt.value
                                ? "border-primary bg-primary-soft"
                                : "border-border bg-surface-raised"
                            )}
                          >
                            <div className="text-sm font-medium text-foreground">
                              {opt.label}
                            </div>
                            <div className="mt-0.5 text-[11px] text-foreground-muted">
                              {opt.desc}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {needPath.calm ? (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-foreground">
                        {t("onboarding.pathwayCalmLabel")}
                      </p>
                      <div className="space-y-1.5">
                        {pathwayOptions.map((opt) => (
                          <button
                            key={`calm-${opt.value}`}
                            type="button"
                            onClick={() => {
                              void hapticService.selection();
                              setCalmPathway(opt.value);
                            }}
                            className={cn(
                              "w-full rounded-xl border px-3 py-2 text-left transition-colors",
                              store.calmPathway === opt.value
                                ? "border-primary bg-primary-soft"
                                : "border-border bg-surface-raised"
                            )}
                          >
                            <div className="text-sm font-medium text-foreground">
                              {opt.label}
                            </div>
                            <div className="mt-0.5 text-[11px] text-foreground-muted">
                              {opt.desc}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.lifeTitle")}
                </h1>
                <WhyNote>{t("onboarding.lifeWhy")}</WhyNote>
                <p className="text-xs text-foreground-muted">
                  {t("onboarding.lifeHint")}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {lifeOptions.map((opt) => {
                    const selected = lifeSituations.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          void hapticService.selection();
                          toggleLifeSituation(opt.value);
                        }}
                        className={cn(
                          "rounded-xl border px-2.5 py-2.5 text-left text-xs font-medium leading-snug transition-colors",
                          selected
                            ? "border-primary bg-primary-soft text-primary"
                            : "border-border bg-surface-raised text-foreground"
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.baselineTitle")}
                </h1>
                <WhyNote>{t("onboarding.baselineWhy")}</WhyNote>
                <p className="text-xs leading-snug text-foreground-muted">
                  {t("onboarding.baselineHint")}
                </p>
                <div className="space-y-2">
                  <ScaleSlider
                    compact
                    label={t("onboarding.wellbeing")}
                    value={baseline?.wellbeing ?? null}
                    onChange={(v) => updateBaseline("wellbeing", v)}
                    minLabel={t("onboarding.wellbeingMin")}
                    maxLabel={t("onboarding.wellbeingMax")}
                  />
                  <ScaleSlider
                    compact
                    label={t("onboarding.energy")}
                    value={baseline?.energy ?? null}
                    onChange={(v) => updateBaseline("energy", v)}
                    minLabel={t("onboarding.energyMin")}
                    maxLabel={t("onboarding.energyMax")}
                  />
                  <ScaleSlider
                    compact
                    label={t("onboarding.stress")}
                    value={baseline?.stress ?? null}
                    onChange={(v) => updateBaseline("stress", v)}
                    minLabel={t("onboarding.stressMin")}
                    maxLabel={t("onboarding.stressMax")}
                  />
                </div>
              </>
            )}

            {step === 6 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.timeTitle")}
                </h1>
                <WhyNote>{t("onboarding.timeWhy")}</WhyNote>
                <div className="grid grid-cols-2 gap-2">
                  {timeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        void hapticService.selection();
                        setAvailableMinutes(opt.value);
                      }}
                      className={cn(
                        "rounded-xl border px-3 py-3.5 text-center text-sm font-medium transition-colors",
                        store.availableMinutes === opt.value
                          ? "border-primary bg-primary-soft text-primary"
                          : "border-border bg-surface-raised text-foreground"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 7 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.toneTitle")}
                </h1>
                <WhyNote>{t("onboarding.toneWhy")}</WhyNote>
                <div className="space-y-2">
                  {toneOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        void hapticService.selection();
                        setCoachTone(opt.value);
                      }}
                      className={cn(
                        "w-full rounded-xl border px-3 py-3 text-left transition-colors",
                        store.coachTone === opt.value
                          ? "border-primary bg-primary-soft"
                          : "border-border bg-surface-raised"
                      )}
                    >
                      <div className="text-sm font-medium text-foreground">
                        {opt.label}
                      </div>
                      <div className="mt-0.5 text-xs text-foreground-muted">
                        {opt.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 8 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.whenTitle")}
                </h1>
                <WhyNote>{t("onboarding.whenWhy")}</WhyNote>
                <div className="space-y-1.5">
                  {preferredTimeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        void hapticService.selection();
                        setPreferredTime(opt.value);
                      }}
                      className={cn(
                        "w-full rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors",
                        store.preferredTime === opt.value
                          ? "border-primary bg-primary-soft text-primary"
                          : "border-border bg-surface-raised text-foreground"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 9 && (
              <>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  {t("onboarding.notifTitle")}
                </h1>
                <p className="text-sm leading-snug text-foreground-muted">
                  {t("onboarding.notifBody")}
                </p>
                <div className="space-y-1.5 rounded-xl border border-border bg-surface-raised p-3 text-xs leading-snug text-foreground-muted">
                  <p className="font-medium text-foreground">
                    {t("onboarding.notifLoopTitle")}
                  </p>
                  <p>{t("onboarding.notifLoopBody")}</p>
                </div>
                <div className="space-y-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      void hapticService.selection();
                      setNotificationsEnabled(true);
                    }}
                    className={cn(
                      "w-full rounded-xl py-3 text-sm font-medium",
                      store.notificationsEnabled === true
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface-muted text-foreground"
                    )}
                  >
                    {t("onboarding.notifYes")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void hapticService.selection();
                      setNotificationsEnabled(false);
                    }}
                    className="w-full rounded-xl py-3 text-sm font-medium text-foreground-muted"
                  >
                    {t("onboarding.notifNo")}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="shrink-0 space-y-1.5 px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-1">
        {saveError ? (
          <p className="text-center text-sm text-danger" role="alert">
            {saveError}
          </p>
        ) : null}
        <button
          type="button"
          disabled={!canProceed() || saving}
          onClick={handleNext}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-medium transition-colors",
            canProceed() && !saving
              ? "bg-primary text-primary-foreground"
              : "cursor-not-allowed bg-surface-muted text-foreground-subtle"
          )}
        >
          {saving ? <Spinner size="sm" className="text-primary-foreground" /> : null}
          {nextLabel}
        </button>
      </div>
    </div>
  );
}
